import { useState } from "react";
import Reveal from "@/components/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;

const inputClass =
  "w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm text-zinc-200 placeholder-zinc-600 transition-colors focus:border-beam/60 focus:outline-none";

export default function AccessRequestSection() {
  const [form, setForm] = useState({ email: "", affiliation: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/access-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      setDone(true);
    } catch {
      setError("Something went wrong. Try emailing us directly at info@qpilabs.ai");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="request-access" className="relative pb-28 md:pb-40" data-testid="access-section">
      <div className="container-x">
        <Reveal>
          <div
            className="clip-frame-sm relative overflow-hidden border border-white/10 bg-panel p-8 md:p-14"
            style={{ boxShadow: "inset 0 0 40px rgba(61,107,255,0.12), inset 0 0 0 1px rgba(0,229,255,0.05)" }}
          >
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(40% 55% at 12% 0%, rgba(61,107,255,0.14), transparent 70%), radial-gradient(35% 50% at 95% 100%, rgba(0,229,255,0.08), transparent 70%)",
              }}
            />
            <div className="relative z-10 max-w-2xl">
              <p className="eyebrow mb-4 text-laser">— Request Access</p>
              <h2 className="text-4xl font-semibold tracking-tighter md:text-5xl">
                Get early access to the simulator.
              </h2>
              <p className="mt-5 font-mono text-xs leading-relaxed text-zinc-500">
                Accounts are provisioned manually. Drop your email and a line about your work —
                we&apos;ll reach out.
              </p>

              {done ? (
                <div className="mt-10 border-l-2 border-beam/60 pl-5" data-testid="access-success">
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-beam">
                    — Request received
                  </p>
                  <p className="mt-3 text-xl font-light text-zinc-200">We&apos;ll be in touch.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-10 flex flex-col gap-5" data-testid="access-form" noValidate>
                  <div>
                    <label htmlFor="access-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Email address <span className="text-laser">*</span>
                    </label>
                    <input
                      id="access-email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      placeholder="your@institution.edu"
                      data-testid="access-email-input"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="access-affiliation" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Affiliation <span className="text-zinc-600">(optional)</span>
                    </label>
                    <input
                      id="access-affiliation"
                      name="affiliation"
                      type="text"
                      value={form.affiliation}
                      onChange={onChange}
                      placeholder="University / Lab / Company"
                      data-testid="access-affiliation-input"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="access-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Message <span className="text-zinc-600">(optional)</span>
                    </label>
                    <textarea
                      id="access-message"
                      name="message"
                      rows={3}
                      value={form.message}
                      onChange={onChange}
                      placeholder="What are you working on? (optional)"
                      data-testid="access-message-input"
                      className={`${inputClass} resize-none`}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="access-submit-btn"
                    className="w-full rounded-full border border-beam/50 bg-black/40 px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:border-beam hover:shadow-[0_0_24px_rgba(0,229,255,0.35)] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? "Sending…" : "Request Access"}
                  </button>
                  {error && (
                    <p className="font-mono text-xs leading-relaxed text-laser" data-testid="access-error">
                      {error}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
